import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';

import { langType, surveyType, surveyTypeValues } from '../../../constants/types';
import SurveyBox from '../../SurveyBox';

import styles from './surveyBoxList.module.css';
import { getTranslation, withLang } from '../../../i18n';
import { clearQuestionResponsesAction } from '../../../actions';
import { LONG_DISPLAY_DATE_FORMAT, routes } from '../../../constants';
import { getCompiledPath } from '../../../util';

import hourglassSvg from '../../../images/UH-hourglass.svg';
import closeSvg from '../../../images/UH-close.svg';
import checkSvg from '../../../images/UH-check.svg';

// ended
const endingMessages = {
  [true]: 'frontPage.surveyBoxList.endingMessages.ended',
  [false]: 'frontPage.surveyBoxList.endingMessages.notEnded'
};

const PAGE_SIZE = 3;

const SurveyBoxList = ({
  surveys,
  type,
  translate: t,
  lang
}) => {
  const dispatch = useDispatch();
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const surveyBoxRefs = React.useRef([]);

  React.useEffect(() => {
    if (visibleCount !== PAGE_SIZE) {
      surveyBoxRefs.current[visibleCount - 3].focus();
    }
  }, [visibleCount]);

  const showMoreClickHandler = () => {
    setVisibleCount(visibleCount + PAGE_SIZE);
  };

  const assignRef = (element) => {
    if (element && !surveyBoxRefs.current.includes(element)) {
      surveyBoxRefs.current.push(element);
    }
    return element;
  };

  const getFeedbackState = (survey) => {
    const surveyEnded = !!survey.surveySchedule.ended;
    const { responded } = survey;
    const hasEnoughResponses = !!survey.feedback?.hasEnoughResponses;
    const hasSeenFeedback = survey.feedback?.hasSeenFeedback;
    // Survey has ended and
    if (surveyEnded) {
      // User has responded
      if (responded) {
        // But not enough responses to show feedback
        if (!hasEnoughResponses) {
          return 'NOT_ENOUGH';
        }
        // And user has seen feedback
        return hasSeenFeedback ? 'FEEDBACK_SEEN' : 'FEEDBACK_WAITING'; // But user has not yet viewed their feedback
      }
      return 'NOT_PARTICIPATED';
    }
    // Survey is still open and the user has responded
    if (responded) {
      return 'RESPONDED_WAITING_FOR_END';
    }
    // Survey is still open and the user has not responded
    return 'NOT_ANSWERED';
  };

  const getIcon = (feedbackState) => {
    switch (feedbackState) {
      case 'FEEDBACK_WAITING':
        return <img src={hourglassSvg} alt={t('frontPage.surveyBoxList.feedbackMessages.hourglassAltText')} />;
      case 'NOT_PARTICIPATED':
        return <img src={closeSvg} alt={t('frontPage.surveyBoxList.feedbackMessages.closeAltText')} />;
      case 'NOT_ANSWERED':
        return <img src={hourglassSvg} alt={t('frontPage.surveyBoxList.feedbackMessages.hourglassAltText')} />;
      default:
        return <img src={checkSvg} alt={t('frontPage.surveyBoxList.feedbackMessages.checkAltText')} />;
    }
  };

  const getIconRowText = (feedbackState) => {
    const feedbackMsgPrefix = 'frontPage.surveyBoxList.feedbackMessages.';
    const responseMsgPrefix = 'frontPage.surveyBoxList.responseMessages.';
    switch (feedbackState) {
      case 'FEEDBACK_SEEN':
        return `${feedbackMsgPrefix}feedbackViewed`;
      case 'FEEDBACK_WAITING':
        return `${feedbackMsgPrefix}feedbackWaiting`;
      case 'NOT_PARTICIPATED':
        return `${responseMsgPrefix}notAnswered`;
      case 'NOT_ANSWERED':
        return `${responseMsgPrefix}waiting`;
      default:
        return `${responseMsgPrefix}answered`;
    }
  };

  const getContent = (feedbackState, survey) => {
    const feedbackMsgPrefix = 'frontPage.surveyBoxList.feedbackMessages';
    switch (feedbackState) {
      case 'NOT_ENOUGH':
        return <p>{t(`${feedbackMsgPrefix}.notEnoughResponses`)}</p>;
      case 'FEEDBACK_SEEN':
        return (
          <div className={styles.buttonContainer}>
            <Link className="button--outline button--outline--white" to={getCompiledPath(routes.survey.feedback?.index, { surveyId: survey.id })} aria-describedby={`survey-${survey.id}-title`}>
              {t('frontPage.surveyBoxList.viewFeedback')}
            </Link>
          </div>
        );
      case 'FEEDBACK_WAITING':
        return (
          <div className={styles.buttonContainer}>
            <Link className={`${styles.surveyBoxButton} button`} to={getCompiledPath(routes.survey.feedback?.index, { surveyId: survey.id })} aria-describedby={`survey-${survey.id}-title`}>
              {t('frontPage.surveyBoxList.viewFeedback')}
            </Link>
          </div>
        );
      case 'NOT_PARTICIPATED':
        return <p>{t(`${feedbackMsgPrefix}.notAnswered`)}</p>;
      case 'RESPONDED_WAITING_FOR_END':
        return <p>{t(`${feedbackMsgPrefix}.shownAfterClosed`)}</p>;
      default:
        // Default to answer survey
        return (
          <div className={styles.buttonContainer}>
            <Link className={`${styles.surveyBoxButton} button`} to={getCompiledPath(routes.survey.index, { surveyId: survey.id })} onClick={() => dispatch(clearQuestionResponsesAction())} aria-describedby={`survey-${survey.id}-title`}>
              {t('frontPage.surveyBoxList.answerSurvey')}
            </Link>
          </div>
        );
    }
  };

  return (
    <>
      <h3>{t(`frontPage.surveyBoxList.surveyTypes.${type}`)}</h3>
      <div className={styles.surveyContainer} data-cy="survey-box-list">
        {surveys.slice(0, visibleCount).map((survey) => {
          const endingMessageKey = endingMessages[!!survey.surveySchedule.ended];
          const feedbackState = getFeedbackState(survey);
          const title = getTranslation(survey.title, lang);
          const surveyBoxContent = getContent(feedbackState, survey);
          const icon = getIcon(feedbackState);
          const iconRowText = getIconRowText(feedbackState);

          return (
            <SurveyBox
              key={survey.id}
              titleId={`survey-${survey.id}-title`}
              alternative={!!survey.surveySchedule.ended}
              title={title}
              titleTag="h4"
              clampTitle
              ref={assignRef}
              icon={icon}
              iconRowText={t(iconRowText)}
              text={`${t(endingMessageKey)} ${moment(survey.surveySchedule.ended ? survey.surveySchedule.ended : survey.surveySchedule.endDate).format(LONG_DISPLAY_DATE_FORMAT)}`}
              content={surveyBoxContent}
            />
          );
        })}
      </div>
      {visibleCount < surveys.length && (
        <button
          type="button"
          className={styles.showMoreButton}
          onClick={showMoreClickHandler}
        >
          {t('frontPage.surveyBoxList.showMore')}
        </button>
      )}
    </>
  );
};

SurveyBoxList.propTypes = {
  surveys: PropTypes.arrayOf(surveyType).isRequired,
  type: PropTypes.oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

export default withLang(SurveyBoxList);
